import { Button, Divider, TextField, Typography, Alert, useTheme, Grid } from '@mui/material';
import Joi from 'joi';
import { useState } from 'react';
import clsx from 'clsx';
import ReCAPTCHA from 'react-google-recaptcha-enterprise';
import axios from 'axios';
import { escape as escapeHtml } from 'lodash';
import { useSelector } from 'react-redux';
import { parameters } from 'api/parameter';
import { useStylesContactForm } from '../styles/widgetStyles';
import { getChannelSettingByKey } from '../../routes/generic/Helpers/GenericIndexHelper';

const ContactFormFromCompanyDetailsWidget = ({ token, columns }) => {
	const theme = useTheme();
	const classes = useStylesContactForm(theme);

	const [contactForm, setContactForm] = useState({
		contact: { name: '', lastName: '', email: '', message: '' },
		errors: {},
		submitButtonName: 'Submit',
		isMessageSuccessfullySent: null
	});
	const emailValidation =
		/^[-A-Za-z0-9!#$%&'*+/=?^_`{|}~\s]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;

	const schema = Joi.object({
		// Names may contain any character (lowercase, accents, apostrophes, symbols), so there is no character regex.
		// trim() makes a whitespace-only name validate as '' so it fails as empty; the trimmed value is discarded,
		// so the submitted text is unchanged.
		name: Joi.string()
			.trim()
			.max(30)
			.required()
			.label('Name')
			.error(errors => {
				errors.forEach(err => {
					// Joi 17 reports an empty string as 'string.empty' ('any.empty' was the Joi <=15 code and never
					// matched); the only other failure left is the 30-character limit.
					switch (err.code) {
						case 'string.empty':
							err.message = 'Name cannot be empty. Please provide a valid name.';
							break;
						default:
							err.message = 'Name can have a maximum of 30 characters.';
							break;
					}
				});
				return errors;
			}),
		// Same rules as name: any characters allowed, non-blank, at most 30 characters.
		lastName: Joi.string()
			.trim()
			.max(30)
			.required()
			.label('Last Name')
			.error(errors => {
				errors.forEach(err => {
					switch (err.code) {
						case 'string.empty':
							err.message = 'Last Name cannot be empty. Please provide a valid last name.';
							break;
						default:
							err.message = 'Last Name can have a maximum of 30 characters.';
							break;
					}
				});
				return errors;
			}),
		email: Joi.string()
			.email({ tlds: { allow: false } })
			.regex(emailValidation)
			.label('Email')
			.error(errors => {
				errors.forEach(err => {
					switch (err.code) {
						case 'any.empty':
							err.message = 'Email cannot be empty. Please provide a valid email address.';
							break;
						default:
							err.message = 'Email is not valid. Please enter a valid email address.';
							break;
					}
				});
				return errors;
			}),
		message: Joi.string().min(3).max(300).required().label('Message')
	});
	const [isRecaptchSuccess, setIsRecaptchaSuccess] = useState(false);
	const companyDetailsResponse = useSelector(({ fuse }) => fuse.form.companyDetailsResponse);
	const channelCompanyDetails = useSelector(
		({ fuse }) => fuse.form.channelSettings.value.company.companyContactViewModel
	);
	const { contactEMail } = companyDetailsResponse?.value?.companyContactViewModel || channelCompanyDetails || {};

	const handleChange = ({ currentTarget: input }) => {
		const contact = { ...contactForm.contact };
		contact[input.name] = input.value;

		setContactForm(prevState => ({
			...prevState,
			contact
		}));
	};

	const validate = () => {
		const { error } = schema.validate(contactForm.contact, { abortEarly: false });

		if (!error) return null;

		const errors = {};

		error.details.forEach(detail => {
			errors[detail.path[0]] = detail.message;
		});

		return errors;
	};

	const hideSnackbar = () => {
		setTimeout(() => {
			setContactForm(prevState => ({
				...prevState,
				isMessageSuccessfullySent: null
			}));
		}, 3000);
	};

	const handleSubmit = e => {
		e.preventDefault();

		const errors = validate();
		setContactForm(prevState => ({
			...prevState,
			errors,
			submitButtonName: errors ? prevState.submitButtonName : 'Loading...'
		}));

		if (errors) return;

		const obj = {
			toEmail: token === 'TOKEN' ? contactEMail : channelCompanyDetails?.contactEMail,
			fromEmail: 'no-reply@briisk.io',
			fromName: `${contactForm.contact.name} ${contactForm.contact.lastName}`,
			subject: 'Contact Form',
			plainTextContent: `${contactForm.contact.name} ${contactForm.contact.lastName}\n${contactForm.contact.email}\n${contactForm.contact.message}`,
			// User input can contain any special character, so escape it to render literally rather than as email markup.
			htmlContent: `Hello Team, <br/> <br/> You have received a new enquiry via the Contact Us form.<br/> <br/> <strong>Customer details</strong><br/><br/> <strong>First name:</strong> ${escapeHtml(contactForm.contact.name)} <br/> <strong>Last name:</strong> ${escapeHtml(contactForm.contact.lastName)} <br/> <strong>Email:</strong> ${escapeHtml(contactForm.contact.email)} <br/> <br/> <strong>Message</strong><br/> ${escapeHtml(contactForm.contact.message)}`
		};

		axios.post(`${parameters.serviceUrl}/SendEmail`, obj).then(
			_ => {
				setContactForm(prevState => ({
					...prevState,
					contact: { name: '', lastName: '', email: '', message: '' },
					errors: {},
					submitButtonName: 'Submit',
					isMessageSuccessfullySent: 'true'
				}));
				hideSnackbar();
			},
			_ => {
				setContactForm(prevState => ({
					...prevState,
					errors: {},
					submitButtonName: 'Submit',
					isMessageSuccessfullySent: 'false'
				}));
				hideSnackbar();
			}
		);
	};

	const contactFormBackGroundColor =
		getChannelSettingByKey('Contact-Page-Background-Color') || theme.palette.background.default;

	return (
		<Grid container className="mt-16 mb-16" justifyContent="center" alignItems="center">
			<Grid item xs={12} md={columns || 8} bgcolor={contactFormBackGroundColor} borderRadius={2} padding={4}>
				<Typography variant="h6">Contact Form</Typography>
				<Divider />
				<br />
				<div className={clsx(classes.form)}>
					<div className="mb-24">
						<TextField
							label="Name"
							id="name"
							name="name"
							variant="outlined"
							required
							fullWidth
							value={contactForm.contact.name}
							onChange={handleChange}
							autoComplete="off"
						/>
						<>
							{contactForm.errors && contactForm.errors.name && (
								<div id="name" className="text-red mt-5">
									{contactForm.errors.name}
								</div>
							)}
						</>
					</div>

					<div className="mb-24">
						<TextField
							label="Last name"
							id="lastName"
							name="lastName"
							variant="outlined"
							required
							fullWidth
							value={contactForm.contact.lastName}
							onChange={handleChange}
							autoComplete="off"
						/>
						<>
							{contactForm.errors && contactForm.errors.lastName && (
								<div id="lastName" className="text-red mt-5">
									{contactForm.errors.lastName}
								</div>
							)}
						</>
					</div>

					<div className="mb-24">
						<TextField
							label="Email"
							id="email"
							name="email"
							variant="outlined"
							required
							fullWidth
							value={contactForm.contact.email}
							onChange={handleChange}
							autoComplete="off"
						/>
						<>
							{contactForm.errors && contactForm.errors.email && (
								<div id="email" className="text-red mt-5">
									{contactForm.errors.email}
								</div>
							)}
						</>
					</div>

					<div className="mb-24">
						<TextField
							label="Message"
							id="notes"
							name="message"
							variant="outlined"
							required
							multiline
							rows={5}
							fullWidth
							value={contactForm.contact.message}
							onChange={handleChange}
							autoComplete="off"
						/>
						<>
							{contactForm.errors && contactForm.errors.message && (
								<div id="message" className="text-red mt-5">
									{contactForm.errors.message}
								</div>
							)}
						</>
					</div>

					<ReCAPTCHA
						sitekey={parameters.captchaKey}
						onChange={() => {
							setIsRecaptchaSuccess(true);
						}}
					/>

					{isRecaptchSuccess && contactEMail && companyDetailsResponse?.status === 'success' && (
						<div className="mt-5">
							<Button
								variant="contained"
								color="primary"
								type="submit"
								onClick={e => handleSubmit(e)}
								disabled={contactForm.submitButtonName !== 'Submit'}
								data-ga-label="contact-form-submit"
							>
								{contactForm.submitButtonName}
							</Button>
						</div>
					)}

					<div className="mt-10">
						{contactForm.isMessageSuccessfullySent === 'true' && (
							<Alert severity="success" className={clsx(classes.snackBar)}>
								Your message has been sent.
							</Alert>
						)}
						{contactForm.isMessageSuccessfullySent === 'false' && (
							<Alert severity="error" className={clsx(classes.snackBar)}>
								Failed to send your message.
							</Alert>
						)}
					</div>
				</div>
			</Grid>
		</Grid>
	);
};

export default ContactFormFromCompanyDetailsWidget;
